import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import logoImage from '../assets/Frame 1000005895.png';
import cardImage from '../assets/Mask group.png';
import upgradeButtonImage from '../assets/Frame 9.png';
import projectsButtonImage from '../assets/Frame 5.png';
import languageButtonImage from '../assets/Frame 1000005885 (1).png';
import profileButtonImage from '../assets/Frame 2 (1).png';
import LanguageModal from '../components/LanguageModal';
import ProfileMenu from '../components/ProfileMenu';
import LoginModal from '../components/LoginModal.js';
import axios from 'axios';

const ProjectsPage = () => {
  const navigate = useNavigate();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState({ name: 'English', code: 'en-IN', flag: '🇮🇳' });
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [updatingTitle, setUpdatingTitle] = useState(false);
  
  // Get the user data when component mounts
  useEffect(() => {
    const getUserFromStorage = () => {
      const storedUser = localStorage.getItem('currentUser');
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          setCurrentUser(parsedUser);
          return parsedUser;
        } catch (error) {
          console.error('Error parsing stored user:', error);
          return null;
        }
      }
      return null;
    };
    
    const user = getUserFromStorage();
    if (!user) {
      setError('User not logged in');
      setLoading(false);
    }
  }, []);
  
  // Fetch user projects when currentUser is available
  useEffect(() => {
    const fetchUserProjects = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Get userId
        const userId = currentUser.id || currentUser.user_id;
        
        if (!userId) {
          setError('User ID not found');
          setLoading(false);
          return;
        }
        
        console.log('Fetching projects for user:', userId);
        
        // Fetch from API
        const response = await axios.get(`https://api.lab.battlexo.com/api/v1/groq/sessions/${userId}`);
        
        if (response.data && Array.isArray(response.data)) {
          console.log('Projects loaded successfully:', response.data);
          setSessions(response.data);
        } else {
          console.error('Invalid data format from API:', response.data);
          setError('Invalid data received from server');
          setSessions([]);
        }
      } catch (err) {
        console.error('Failed to load projects:', err);
        setError('Failed to load your projects. Please try again later.');
        
        // Fallback to localStorage
        const fallbackSessions = getFallbackSessionsFromStorage();
        if (fallbackSessions.length > 0) {
          console.log('Using fallback sessions from localStorage');
          setSessions(fallbackSessions);
        } else {
          setSessions([]);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserProjects();
  }, [currentUser]);
  
  // Get fallback sessions from localStorage if API fails
  const getFallbackSessionsFromStorage = () => {
    if (!currentUser) return [];
    
    const userId = currentUser.id || currentUser.user_id;
    const fallbackSessions = [];
    
    // Scan localStorage for chat sessions
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith(`chat_${userId}_`)) {
        try {
          const sessionData = JSON.parse(localStorage.getItem(key));
          if (sessionData && sessionData.id) {
            const sessionId = key.split(`chat_${userId}_`)[1];
            fallbackSessions.push({
              session_id: sessionId,
              user_id: userId,
              title: sessionData.title || 'Untitled Project',
              created_at: sessionData.createdAt || new Date().toISOString(),
              updated_at: sessionData.updatedAt || new Date().toISOString(),
              message_count: sessionData.messages?.length || 0,
              isRemix: sessionData.isRemix || false
            });
          }
        } catch (e) {
          console.error('Error parsing session data:', e);
        }
      }
    }
    
    return fallbackSessions;
  };
  
  // Format date to readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  
  const goToUpgrade = () => {
    navigate('/upgrade');
  };

  const openLanguageModal = () => {
    setIsLanguageModalOpen(true);
  };

  const closeLanguageModal = () => {
    setIsLanguageModalOpen(false);
  };
  
  const openLoginModal = () => {
    setIsLoginModalOpen(true);
  };
  
  const closeLoginModal = () => {
    setIsLoginModalOpen(false);
  };
  
  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };
  
  const closeProfileMenu = () => {
    setIsProfileMenuOpen(false);
  };
  
  const handleLanguageChange = (language) => {
    setCurrentLanguage(language);
    console.log(`Language changed to: ${language.name} (${language.code})`);
  };

  // Navigate to chat with session ID
  const navigateToSession = (sessionId) => {
    navigate(`/chat`, { 
      state: { sessionId: sessionId }
    });
  };
  
  // Function to navigate to search page when logo is clicked
  const navigateToSearch = () => {
    navigate('/');
  };
  
  // Handle profile button click based on login status
  const handleProfileButtonClick = () => {
    if (currentUser) {
      toggleProfileMenu();
    } else {
      openLoginModal();
    }
  };

  // Update title of a session using the API
  const updateSessionTitle = async (sessionId, newTitle) => {
    if (!sessionId || !newTitle.trim()) return;
    
    setUpdatingTitle(true);
    
    try {
      // Create the URL with query parameter for title as shown in the CURL example
      const url = `https://api.lab.battlexo.com/api/v1/groq/session/${sessionId}/title?title=${encodeURIComponent(newTitle.trim())}`;
      
      // Use the PUT method as specified in the CURL example
      const response = await axios({
        method: 'PUT',
        url: url,
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.status === 200) {
        // Update local state
        setSessions(prevSessions => 
          prevSessions.map(session => 
            session.session_id === sessionId 
              ? { ...session, title: newTitle.trim() } 
              : session
          )
        );
        console.log('Session title updated successfully');
      } else {
        console.error('Failed to update session title:', response);
      }
    } catch (err) {
      console.error('Error updating session title:', err);
      
      // If we get a network error, update locally as fallback
      if (err.message.includes('Network Error')) {
        console.log('Network error, updating session title in localStorage as fallback');
        setSessions(prevSessions => 
          prevSessions.map(session => 
            session.session_id === sessionId 
              ? { ...session, title: newTitle.trim() } 
              : session
          )
        );
      }
    } finally {
      setUpdatingTitle(false);
      setEditingSessionId(null);
    }
  };
  
  // Start editing a title
  const handleEditTitle = (e, sessionId, currentTitle) => {
    e.stopPropagation(); // Prevent navigating to session
    setEditingSessionId(sessionId);
    setEditingTitle(currentTitle || 'Untitled Project');
  };
  
  // Handle title input change
  const handleTitleChange = (e) => {
    setEditingTitle(e.target.value);
  };
  
  // Handle title input key press
  const handleTitleKeyPress = (e) => {
    if (e.key === 'Enter') {
      updateSessionTitle(editingSessionId, editingTitle);
    } else if (e.key === 'Escape') {
      setEditingSessionId(null);
    }
  };
  
  // Handle clicking outside of the input field
  const handleTitleBlur = () => {
    if (editingSessionId) {
      updateSessionTitle(editingSessionId, editingTitle);
    }
  };

  return (
    <PageContainer>
      <Header>
        <LogoSection>
          <Logo src={logoImage} alt="Total AI" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
        </LogoSection>
        <NavSection>
          <UpgradeButton onClick={goToUpgrade}>
            <UpgradeButtonImg src={upgradeButtonImage} alt="Upgrade" />
          </UpgradeButton>
          <ProjectsButton>
            <ProjectsButtonImg src={projectsButtonImage} alt="My Projects" />
          </ProjectsButton>
          <ProfileButton onClick={handleProfileButtonClick}>
            <ProfileButtonImg src={profileButtonImage} alt="Profile" />
          </ProfileButton>
        </NavSection>
      </Header>

      <ContentContainer>
        <ContentHeader>
          <SectionTitle>My Projects</SectionTitle>
        </ContentHeader>

        {loading ? (
          <LoadingContainer>
            <LoadingMessage>Loading your projects...</LoadingMessage>
          </LoadingContainer>
        ) : error ? (
          <ErrorContainer>
            <ErrorMessage>{error}</ErrorMessage>
            {!currentUser && (
              <LoginPrompt>
                Please <LoginLink onClick={openLoginModal}>sign in</LoginLink> to view your projects
              </LoginPrompt>
            )}
          </ErrorContainer>
        ) : sessions.length === 0 ? (
          <EmptyContainer>
            <EmptyMessage>
              You don't have any projects yet. 
              <br key="line-break" />
              <MetaLink key="create-link" onClick={() => navigate('/chat')}>Create your first project</MetaLink>
            </EmptyMessage>
          </EmptyContainer>
        ) : (
          <ProjectGrid>
            {sessions.map(session => (
              <ProjectCard 
                key={session.session_id} 
                onClick={() => navigateToSession(session.session_id)}
              >
                <ProjectImageContainer>
                  <ProjectThumbnail src={cardImage} alt={session.title || 'Untitled Project'} />
                </ProjectImageContainer>
                <ProjectInfo>
                  {editingSessionId === session.session_id ? (
                    <TitleEditContainer>
                      <TitleInput 
                        value={editingTitle}
                        onChange={handleTitleChange}
                        onKeyDown={handleTitleKeyPress}
                        onBlur={handleTitleBlur}
                        autoFocus
                        disabled={updatingTitle}
                      />
                    </TitleEditContainer>
                  ) : (
                    <ProjectTitle>
                      {session.title || 'Untitled Project'} 
                      <EditIcon onClick={(e) => handleEditTitle(e, session.session_id, session.title)}>✎</EditIcon>
                      {(session.isRemix || session.title?.includes('Remix')) && 
                        <RemixBadge>Remix</RemixBadge>
                      }
                    </ProjectTitle>
                  )}
                  <ProjectStats>
                    <StatButton 
                      key="play"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToSession(session.session_id);
                      }}
                    >
                      <PlayIcon>▶</PlayIcon> Play
                    </StatButton>
                  </ProjectStats>
                  <ProjectMeta>
                    <MetaText key="created">Created: <MetaLink>{formatDate(session.created_at)}</MetaLink></MetaText>
                    <MetaText key="updated">Updated: <MetaLink>{formatDate(session.updated_at)}</MetaLink></MetaText>
                    {session.message_count && (
                      <MetaText key="messages">Messages: <MetaLink>{session.message_count}</MetaLink></MetaText>
                    )}
                  </ProjectMeta>
                </ProjectInfo>
              </ProjectCard>
            ))}
          </ProjectGrid>
        )}
      </ContentContainer>

      <LanguageModal 
        isOpen={isLanguageModalOpen} 
        onClose={closeLanguageModal} 
        onLanguageChange={handleLanguageChange}
      />
      
      <ProfileMenu
        isOpen={isProfileMenuOpen}
        onClose={closeProfileMenu}
      />
      
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={closeLoginModal}
      />
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  max-width: 100%;
  min-height: 100vh;
  background-color: #ffffff;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #eaeaea;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled.img`
  height: 24px;
`;

const Title = styled.span`
  font-weight: 700;
  font-size: 18px;
  margin-left: 4px;
  background: linear-gradient(90deg, #5B68DF 0%, #A968DF 100%);
  -webkit-background-clip: text;
  color: transparent;
`;

const NavSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UpgradeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const UpgradeButtonImg = styled.img`
  height: 36px;
  width: auto;
`;

const ProjectsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const ProjectsButtonImg = styled.img`
  height: 36px;
  width: auto;
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const ButtonImage = styled.img`
  height: 36px;
  width: auto;
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const ProfileButtonImg = styled.img`
  height: 36px;
  width: auto;
`;

const ContentContainer = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
`;

const ContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const SectionTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #111;
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.25rem;
  width: 100%;
`;

const ProjectCard = styled.div`
  background-color: #23232D;
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(-2px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  }
`;

const ProjectImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  background-color: #000;
  overflow: hidden;
`;

const ProjectTitle3D = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 24px;
  font-weight: 900;
  line-height: 0.9;
  color: #30FC75; /* Neon green color */
  text-shadow: 0 0 5px rgba(48, 252, 117, 0.7);
  z-index: 1;
  letter-spacing: -1px;
  text-transform: uppercase;
`;

const ProjectThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  
`;

const ProjectInfo = styled.div`
  padding: 1rem;
  background: white;
`;

const ProjectTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #000;
  margin: 0 0 0.75rem 0;
`;

const InfoIcon = styled.span`
  font-size: 0.875rem;
  color: #666;
  cursor: pointer;
  opacity: 0.7;
`;

const ProjectStats = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const StatButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  background-color: #E806941A;
  color: ${props => props.$secondary ? '#FF9500' : '#000'};
  border: 1px solid #E80694;
  padding: 0.5rem 1rem;
  border-radius: 10px;
  font-weight: bold;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
 
  &:active {
    transform: translateY(0);
  }
`;

const PlayIcon = styled.span`
  font-size: 0.75rem;
`;

const ShareIcon = styled.span`
  font-size: 0.875rem;
`;

const ProjectMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.25rem;
`;

const MetaText = styled.div`
  font-size: 0.875rem;
  color: #666;
  line-height: 1.5;
`;

const MetaLink = styled.span`
  color: #3B82F6;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

// Add styled components for loading, error, and empty states
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const LoadingMessage = styled.div`
  font-size: 18px;
  color: #666;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  padding: 20px;
`;

const ErrorMessage = styled.div`
  font-size: 18px;
  color: #e53935;
  text-align: center;
`;

const EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  padding: 20px;
`;

const EmptyMessage = styled.div`
  font-size: 18px;
  color: #666;
  text-align: center;
`;

// New components for login prompts
const LoginPrompt = styled.div`
  margin-top: 20px;
  font-size: 16px;
  color: #666;
  text-align: center;
`;

const LoginLink = styled.span`
  color: #5B68DF;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    color: #8A68DF;
  }
`;

// Add new styled components
const TitleEditContainer = styled.div`
  width: 100%;
  margin-bottom: 0.75rem;
`;

const TitleInput = styled.input`
  width: 100%;
  padding: 0.25rem;
  font-size: 1rem;
  font-weight: 600;
  border: 1px solid #ddd;
  border-radius: 4px;
  outline: none;
  font-family: 'Space Grotesk', sans-serif;
  
  &:focus {
    border-color: #E80694;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const EditIcon = styled.span`
  font-size: 0.875rem;
  color: #666;
  cursor: pointer;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
    color: #E80694;
  }
`;

const RemixBadge = styled.span`
  background-color:#E806941A;
  color: #000;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  margin-left: 0.5rem;
`;

export default ProjectsPage;